import { AmountUsd } from '@ui/components/Amount/AmountUsd';
import { TextTooltip } from '@ui/components/TextTooltip';
import { usePayTokenPriceLatest } from '@ui/features/Trade/hooks/usePayTokenPriceLatest';
import { usePriceLive } from '@ui/hooks/price/usePriceLive';
import { useTradePairInfosByPairBase } from '@ui/hooks/trade/useTradePairInfos';
import { Position } from '@ui/services/contract/hooks/TradingReaderFacet/useGetPositionV2/types';

import { usePositionListContext } from './context';
import { NetValueTooltipContent } from './NetValueTooltipContent';
import { valueStyle } from './styles';
import { PositionMarketData } from './types';

export const NetValue = ({
  position,
  marketData,
}: {
  position: Position;
  marketData?: PositionMarketData;
}) => {
  if (marketData) {
    return <NetValueValue position={position} marketData={marketData} />;
  }

  return <NetValueWithHooks position={position} />;
};

const NetValueWithHooks = ({ position }: { position: Position }) => {
  const { feeConfig } = usePositionListContext();
  const pairMarketPrice = usePriceLive(position.pair.baseSymbol);

  const collateralMarketPrice = usePayTokenPriceLatest(
    position.collateralToken,
  );
  const tradePair = useTradePairInfosByPairBase(position.pair.address);
  const marketData = {
    pairMarketPrice,
    collateralMarketPrice,
    tradePair,
  };

  return (
    <NetValueValue
      position={position}
      marketData={marketData}
      feeConfig={feeConfig}
    />
  );
};

const NetValueValue = ({
  position,
  marketData,
  feeConfig: feeConfigProp,
}: {
  position: Position;
  marketData: PositionMarketData;
  feeConfig?: ReturnType<typeof usePositionListContext>['feeConfig'];
}) => {
  const feeConfig = feeConfigProp ?? marketData.tradePair?.feeConfig;
  const pairMarketPrice = marketData.pairMarketPrice;
  const collateralMarketPrice = marketData.collateralMarketPrice;
  const tradePair = marketData.tradePair;

  if (!feeConfig || !collateralMarketPrice || !tradePair) {
    return null;
  }

  return (
    <TextTooltip
      content={
        <NetValueTooltipContent position={position} marketData={marketData} />
      }
    >
      <AmountUsd
        css={valueStyle}
        value={
          pairMarketPrice &&
          position.getNetValue({
            pairMarketPrice,
            collateralMarketPrice,
            tradePair,
            ...feeConfig,
          })
        }
        data-cy="NetValue"
        data-cy-value={
          pairMarketPrice &&
          position.getNetValue({
            pairMarketPrice,
            collateralMarketPrice,
            tradePair,
            ...feeConfig,
          })
        }
      />
    </TextTooltip>
  );
};
